import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api from "../lib/api";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import {
  Activity, AlertTriangle, CheckCircle2, Clock, Loader2, RefreshCw, TrendingUp,
} from "lucide-react";

/**
 * Pack 5.5 · AI Jobs Monitor
 *
 * Real-time health dashboard for every AI-generated deliverable. Binds to
 * GET /api/admin/ai-jobs which returns the shape produced by
 * `backend.ai_monitor.compute_ai_health`. Auto-refreshes every 15s. Adding
 * a new AI feature (Proposal Builder™, Campaign Kit™, …) surfaces here
 * automatically once it's added to the AI_JOB_REGISTRY.
 */
const REFRESH_MS = 15000;

export default function AIJobsMonitor() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const timerRef = useRef(null);

  const load = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    if (silent) setRefreshing(true);
    try {
      const { data: res } = await api.get("/admin/ai-jobs");
      setData(res);
      setError(null);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to load AI health");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    load();
    timerRef.current = setInterval(() => load({ silent: true }), REFRESH_MS);
    return () => timerRef.current && clearInterval(timerRef.current);
  }, [load]);

  if (loading) {
    return (
      <div className="py-16 flex flex-col items-center text-muted-foreground">
        <Loader2 className="w-6 h-6 animate-spin text-accent" />
        <div className="mt-3 text-sm">Loading AI health…</div>
      </div>
    );
  }
  if (error) {
    return <Card className="p-6 text-sm text-red-300" data-testid="ai-monitor-error">{error}</Card>;
  }
  if (!data) return null;

  return (
    <div className="space-y-6" data-testid="ai-jobs-monitor">
      {/* Top-level totals */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Metric icon={Activity}       label="Total AI jobs"   value={data.totals.total}      accent="text-foreground" testId="ai-totals-total" />
        <Metric icon={Loader2}        label="Generating now"  value={data.totals.generating} accent="text-amber-300"  testId="ai-totals-generating" pulse={data.totals.generating > 0} />
        <Metric icon={CheckCircle2}   label="Succeeded"       value={data.totals.succeeded}  accent="text-emerald-300" testId="ai-totals-succeeded" />
        <Metric icon={AlertTriangle}  label="Failed"          value={data.totals.failed}     accent={data.totals.failed > 0 ? "text-red-300" : "text-muted-foreground"} testId="ai-totals-failed" />
      </div>

      {/* Per-deliverable panels */}
      <div className="grid gap-4 md:grid-cols-2">
        {data.deliverables.map((d) => (
          <DeliverableCard key={`${d.collection}.${d.field}`} d={d} />
        ))}
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between text-[10px] text-muted-foreground uppercase tracking-widest">
        <span data-testid="ai-monitor-refreshed-at">Last refreshed {new Date(data.generated_at).toLocaleTimeString()}</span>
        <Button variant="outline" size="sm" onClick={() => load({ silent: true })} disabled={refreshing} data-testid="ai-monitor-refresh">
          {refreshing ? <Loader2 className="w-3 h-3 mr-1.5 animate-spin" /> : <RefreshCw className="w-3 h-3 mr-1.5" />}
          Refresh
        </Button>
      </div>
    </div>
  );
}

function Metric({ icon: Icon, label, value, accent, pulse, testId }) {
  return (
    <Card className="p-4" data-testid={testId}>
      <div className="flex items-center gap-2 overline">
        <Icon className={`w-3.5 h-3.5 ${accent} ${pulse ? "animate-pulse" : ""}`} /> {label}
      </div>
      <div className={`font-display text-3xl mt-2 ${accent}`}>{value ?? "—"}</div>
    </Card>
  );
}

function DeliverableCard({ d }) {
  const counts = d.counts || {};
  const rows = useMemo(() => Object.entries(counts).sort((a, b) => b[1] - a[1]), [counts]);
  const badge = d.failed > 0
    ? { text: `${d.failed} failed`, tone: "bg-red-500/15 border-red-500/40 text-red-300" }
    : d.generating > 0
      ? { text: `${d.generating} in flight`, tone: "bg-amber-500/15 border-amber-500/40 text-amber-300" }
      : { text: "Healthy", tone: "bg-emerald-500/15 border-emerald-500/40 text-emerald-300" };

  return (
    <Card className="p-5" data-testid={`ai-deliverable-${d.field}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="overline">{d.label}</div>
          <div className="text-xs text-muted-foreground mt-1">{d.collection}.{d.field}</div>
        </div>
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest border ${badge.tone}`}>
          {badge.text}
        </span>
      </div>

      {/* Status breakdown */}
      <div className="mt-4 grid grid-cols-2 gap-2 text-xs">
        {rows.length === 0 && <div className="text-muted-foreground italic col-span-2">No jobs yet.</div>}
        {rows.map(([status, n]) => (
          <div key={status} className="flex items-center justify-between px-2 py-1 rounded bg-secondary/40 border border-border/50">
            <span className="capitalize">{status.replace(/_/g, " ")}</span>
            <span className="font-medium">{n}</span>
          </div>
        ))}
      </div>

      {/* Timing + retry health */}
      <div className="mt-4 grid grid-cols-3 gap-2 text-xs">
        <TimingChip icon={Clock}      label="Avg"    value={fmtSeconds(d.avg_generation_seconds)} />
        <TimingChip icon={TrendingUp} label="p95"    value={fmtSeconds(d.p95_generation_seconds)} />
        <TimingChip icon={RefreshCw}  label="Retry"  value={fmtRetry(d.retry_rate)} />
      </div>

      {/* Last error */}
      {d.last_error && (
        <div className="mt-4 rounded-md border border-red-500/40 bg-red-500/5 p-3 text-xs" data-testid={`ai-last-error-${d.field}`}>
          <div className="flex items-center gap-2 overline text-red-300">
            <AlertTriangle className="w-3 h-3" /> Last error
          </div>
          <div className="mt-2 leading-relaxed break-words">{d.last_error.message}</div>
          <div className="mt-2 flex items-center justify-between text-[10px] text-muted-foreground">
            <span>Doc: {d.last_error.id?.slice(0, 8)}… · attempts {d.last_error.attempts ?? "—"}</span>
            <span>{d.last_error.at ? new Date(d.last_error.at).toLocaleString() : ""}</span>
          </div>
        </div>
      )}
    </Card>
  );
}

function TimingChip({ icon: Icon, label, value }) {
  return (
    <div className="flex flex-col rounded bg-secondary/40 border border-border/50 px-2 py-1.5">
      <div className="flex items-center gap-1 text-[10px] uppercase tracking-widest text-muted-foreground">
        <Icon className="w-3 h-3" /> {label}
      </div>
      <div className="text-sm font-medium mt-0.5">{value}</div>
    </div>
  );
}

function fmtSeconds(v) {
  if (v === null || v === undefined) return "—";
  if (v < 1) return `${Math.round(v * 1000)} ms`;
  if (v < 60) return `${v.toFixed(1)} s`;
  const m = Math.floor(v / 60);
  const s = Math.round(v % 60);
  return `${m}m ${s}s`;
}
function fmtRetry(v) {
  if (v === null || v === undefined) return "—";
  return `${Math.round(v * 100)}%`;
}
